import React, { useEffect, useState, useCallback } from 'react';
import { debounce } from 'lodash';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Input,
  InputAdornment,
  Tooltip,
  Grid,
  Typography,
  Chip,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Stack,
  Paper,
  Container,
  Fade,
  Zoom,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ChatIcon from '@mui/icons-material/Chat';
import PersonIcon from '@mui/icons-material/Person';
import DataTable from 'react-data-table-component';
import { ChatApi } from '../../services/AllServices';
import errorHandler from '../../utils/errorHandler';
import { IMAGE_BASE } from '../../utils/constants';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader } from '../../components/CustomLoader';
import { TrimText } from '../../utils/helpers';
import ChatDialog from './ChatView';
import BackButton from '../../components/Buttons/BackButton';

export default function FriendList() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { id: userId } = useParams(); // logged-in user ID
    const [searchItem, setSearchItem] = useState('');
    const [data, setData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [isLoading, setIsLoading] = useState(false);
    const [loaderDelay, setLoaderDelay] = useState(false);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [totalFriends, setTotalFriends] = useState(0);

    const [openChat, setOpenChat] = useState(false);
    const [selectedFriend, setSelectedFriend] = useState({ id: null, name: '' });
    const [messages, setMessages] = useState([]);

    // Fetch list of chat persons
    const fetchUsers = async () => {
        try {
            setIsLoading(true);
            const res = await ChatApi.getChatPersonsList({ userId });
            if (res.status === 200) {
                const friends = res.data.data.records || res.data.data;
                setData(friends);
                setTotalPages(res.data.data.totalRecords || friends.length);
                // Use total records from API response for accurate statistics
                setTotalFriends(res.data.data.totalRecords || friends.length);
            }
            setLoaderDelay(true);
        } catch (err) {
            errorHandler(err);
            setLoaderDelay(false);
        } finally {
            setIsLoading(false);
        }
    };

    const debouncedSearch = useCallback(
        debounce(() => fetchUsers(), 400),
        [searchItem, currentPage, pageSize]
    );

    const handlePageChange = (page) => {
        setCurrentPage(page);
        // Update URL parameter to maintain page state
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('page', page);
        setSearchParams(newSearchParams);
    };

    // Initialize state from URL parameters
    useEffect(() => {
        const pageFromUrl = searchParams.get('page');
        const viewModeFromUrl = searchParams.get('view');
        const searchFromUrl = searchParams.get('search');
        
        if (pageFromUrl && !isNaN(pageFromUrl) && parseInt(pageFromUrl) > 0) {
            setCurrentPage(parseInt(pageFromUrl));
        }
        
        if (viewModeFromUrl && (viewModeFromUrl === 'grid' || viewModeFromUrl === 'list')) {
            setViewMode(viewModeFromUrl);
        }
        
        if (searchFromUrl) {
            setSearchItem(searchFromUrl);
        }
    }, [searchParams]);

    useEffect(() => {
        debouncedSearch();
    }, [searchItem, currentPage, pageSize]);

    const renderFriendCard = (friend, index) => (
        <Zoom in={!isLoading} className={`friend-card-zoom friend-card-zoom-${index}`}>
            <Grid item xs={12} sm={6} md={4} lg={3} key={friend._id}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        borderRadius: 3,
                        background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.05) 100%)',
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                            borderColor: 'primary.main',
                            transform: 'translateY(-4px)',
                            boxShadow: '0 12px 40px rgba(0, 161, 221, 0.2)',
                        }
                    }}
                >
                    {/* Header */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Avatar
                            src={`${IMAGE_BASE}${friend.image}`}
                            sx={{
                                width: 48,
                                height: 48,
                                background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                                boxShadow: '0 4px 16px rgba(0, 161, 221, 0.3)'
                            }}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/assets/images/avatars/avatar_2.jpg';
                            }}
                        >
                            <PersonIcon sx={{ fontSize: 24, color: 'white' }} />
                        </Avatar>
                        <Chip
                            label="Friend"
                            size="small"
                            sx={{
                                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                color: '#4caf50',
                                fontWeight: 600,
                                border: '1px solid',
                                borderColor: 'rgba(76, 175, 80, 0.3)'
                            }}
                        />
                    </Stack>

                    {/* Content */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                            {friend.username || 'N/A'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                            {`${friend.firstname} ${friend.lastname}` || 'N/A'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                            {friend.email || 'N/A'}
                        </Typography>
                    </Box>

                    {/* Actions */}
                    <Stack direction="row" spacing={1}>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<VisibilityIcon />}
                            onClick={() => {
                                setSelectedFriend({
                                    id: friend._id,
                                    name: `${friend.firstname} ${friend.lastname}` || friend.username,
                                });
                                fetchChatMessages(friend._id);
                                setOpenChat(true);
                            }}
                            sx={{
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #0091c8 0%, #3b8fd9 100%)',
                                }
                            }}
                        >
                            View Chat
                        </Button>
                    </Stack>
                </Paper>
            </Grid>
        </Zoom>
    );

    // Fetch chat messages for a friend
    const fetchChatMessages = async (friendId) => {
        try {
            const res = await ChatApi.getChatHistory({ userId, friendId });
            if (res.status === 200) {
                setMessages(res.data.data || []);
            }
        } catch (err) {
            errorHandler(err);
        }
    };

    // Delete a single message
    const handleDeleteMessage = async (messageId) => {
        try {
            const res = await ChatApi.deleteMessage({ id: messageId });
            if (res.status === 200) {
                setMessages((prev) => prev.filter((msg) => msg._id !== messageId));
            }
        } catch (err) {
            errorHandler(err);
        }
    };

    // Clear chat history
    const handleClearHistory = async (userId, friendId) => {
        try {
            const res = await ChatApi.deleteChat({ userId, friendId });
            if (res.status === 200) {
                setMessages([]);
            }
        } catch (err) {
            errorHandler(err);
        }
    };

    const columns = [
        {
            name: 'S.No.',
            cell: (row, idx) => (currentPage - 1) * pageSize + idx + 1,
            width: '100px',
        },
        {
            name: 'User Name',
            selector: (row) => row.username || '-',
            sortable: true,
        },
        {
            name: 'Name',
            selector: (row) => `${row.firstname} ${row.lastname}` || '-',
            sortable: true,
        },
        {
            name: 'Email',
            selector: (row) => row.email || '-',
            sortable: true,
        },
        {
            name: 'Avatar',
            cell: (row) => (
                <Avatar
                    src={`${IMAGE_BASE}${row.image}`}
                    sx={{ width: 40, height: 40 }}
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/assets/images/avatars/avatar_2.jpg';
                    }}
                />
            ),
            width: '80px',
        },
        {
            name: 'Action',
            right: true,
            cell: (row) => (
                <Tooltip title="View Chat">
                    <IconButton
                        color="primary"
                        onClick={() => {
                            setSelectedFriend({
                                id: row._id,
                                name: `${row.firstname} ${row.lastname}` || row.username,
                            });
                            fetchChatMessages(row._id);
                            setOpenChat(true);
                        }}
                    >
                        <VisibilityIcon />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Fade in timeout={500}>
                <Stack spacing={4}>
                    {/* Header Section */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
                            border: '1px solid',
                            borderColor: 'divider',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '4px',
                                background: 'linear-gradient(90deg, #00a1dd 0%, #42a5f5 50%, #90caf9 100%)',
                            }
                        }}
                    >
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={3}>
                                <BackButton variant="h6" />
                                <Avatar
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                                        boxShadow: '0 8px 32px rgba(0, 161, 221, 0.4)'
                                    }}
                                >
                                    <ChatIcon sx={{ fontSize: 32, color: 'white' }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                                        Friend List
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                        View and manage user's chat friends and conversations
                                    </Typography>
                                </Box>
                            </Stack>
                        </Stack>

                        {/* Statistics Cards */}
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        borderRadius: 2,
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        border: '1px solid',
                                        borderColor: 'rgba(255, 255, 255, 0.1)',
                                        textAlign: 'center'
                                    }}
                                >
                                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                                        {totalFriends}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                        Total Friends
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        borderRadius: 2,
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        border: '1px solid',
                                        borderColor: 'rgba(255, 255, 255, 0.1)',
                                        textAlign: 'center'
                                    }}
                                >
                                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                                        {totalPages}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                        Total Records
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Controls Section */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            background: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid',
                            borderColor: 'divider'
                        }}
                    >
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', gap: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
                                <Input
                                    value={searchItem}
                                    placeholder="Search friends..."
                                    onChange={(e) => {
                                        const query = e.target.value;
                                        setSearchItem(query);
                                        setCurrentPage(1);
                                        
                                        // Update URL parameter
                                        const newSearchParams = new URLSearchParams(searchParams);
                                        if (query) {
                                            newSearchParams.set('search', query);
                                        } else {
                                            newSearchParams.delete('search');
                                        }
                                        newSearchParams.set('page', '1');
                                        setSearchParams(newSearchParams);
                                    }}
                                    sx={{
                                        color: 'white',
                                        '& .MuiInput-input': {
                                            color: 'white',
                                        },
                                        '& .MuiInput-input::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.5)',
                                            opacity: 1,
                                        },
                                        '& .MuiInput-root': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            borderRadius: 2,
                                            padding: '8px 16px',
                                        }
                                    }}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => {
                                                    setSearchItem('');
                                                    setCurrentPage(1);
                                                    
                                                    // Update URL parameter
                                                    const newSearchParams = new URLSearchParams(searchParams);
                                                    newSearchParams.delete('search');
                                                    newSearchParams.set('page', '1');
                                                    setSearchParams(newSearchParams);
                                                }}
                                                sx={{ color: 'text.secondary' }}
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                            </Stack>

                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={(e, newMode) => {
                                    if (newMode) {
                                        setViewMode(newMode);
                                        // Update URL parameter to maintain view mode
                                        const newSearchParams = new URLSearchParams(searchParams);
                                        newSearchParams.set('view', newMode);
                                        setSearchParams(newSearchParams);
                                    }
                                }}
                                sx={{
                                    '& .MuiToggleButton-root': {
                                        color: 'text.secondary',
                                        borderColor: 'divider',
                                        '&.Mui-selected': {
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: 'primary.dark',
                                            }
                                        }
                                    }
                                }}
                            >
                                <ToggleButton value="grid">
                                    <ViewModuleIcon />
                                </ToggleButton>
                                <ToggleButton value="list">
                                    <ViewListIcon />
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>
                    </Paper>

                    {/* Content Section */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 3,
                            background: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid',
                            borderColor: 'divider',
                            overflow: 'hidden'
                        }}
                    >
                        {viewMode === 'grid' ? (
                            /* Grid View */
                            <Box sx={{ p: 3 }}>
                                {isLoading || !loaderDelay ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                        <Loader />
                                    </Box>
                                ) : !data || data.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 8 }}>
                                        <Avatar
                                            sx={{
                                                width: 80,
                                                height: 80,
                                                mx: 'auto',
                                                mb: 3,
                                                background: 'linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)',
                                                boxShadow: '0 8px 32px rgba(156, 39, 176, 0.3)'
                                            }}
                                        >
                                            <ChatIcon sx={{ fontSize: 40 }} />
                                        </Avatar>
                                        <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                                            No friends found
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            This user hasn't added any friends yet
                                        </Typography>
                                    </Box>
                                ) : (
                                    <>
                                        <Grid container spacing={3}>
                                            {data.map((friend, index) => renderFriendCard(friend, index))}
                                        </Grid>
                                        
                                        {/* Pagination Controls (Grid View) */}
                                        <Box sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center', 
                                            gap: 2,
                                            flexWrap: 'wrap',
                                            mt: 4,
                                            p: 2,
                                            borderRadius: 2,
                                            background: 'rgba(255, 255, 255, 0.02)',
                                            border: '1px solid',
                                            borderColor: 'divider'
                                        }}>
                                            {/* Center - Item Range Display */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" sx={{ color: 'white' }}>
                                                    {totalPages > 0 ? (
                                                        `Showing ${((currentPage || 1) - 1) * pageSize + 1} to ${Math.min((currentPage || 1) * pageSize, totalPages)} of ${totalPages} friends`
                                                    ) : (
                                                        'No friends found'
                                                    )}
                                                </Typography>
                                            </Box>
                                            {/* Left side - Page Size Selector */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" sx={{ color: 'white', whiteSpace: 'nowrap' }}>
                                                    Items per page:
                                                </Typography>
                                                <FormControl size="small">
                                                    <Select
                                                        value={pageSize}
                                                        onChange={(e) => {
                                                            setPageSize(e.target.value);
                                                            setCurrentPage(1);
                                                            const newSearchParams = new URLSearchParams(searchParams);
                                                            newSearchParams.set('pageSize', e.target.value);
                                                            newSearchParams.set('page', '1');
                                                            setSearchParams(newSearchParams);
                                                        }}
                                                        displayEmpty
                                                        sx={{
                                                            color: 'white',
                                                            minWidth: 80,
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(255, 255, 255, 0.7)',
                                                            },
                                                            '& .MuiSvgIcon-root': {
                                                                color: 'white',
                                                            },
                                                        }}
                                                    >
                                                        <MenuItem value={2}>2</MenuItem>
                                                        <MenuItem value={5}>5</MenuItem>
                                                        <MenuItem value={10}>10</MenuItem>
                                                        <MenuItem value={20}>20</MenuItem>
                                                        <MenuItem value={50}>50</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Box>

                                            {/* Right side - Navigation Controls */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    disabled={totalPages === 0 || (currentPage || 1) === 1}
                                                    onClick={() => handlePageChange((currentPage || 1) - 1)}
                                                    sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                                                >
                                                    Previous
                                                </Button>
                                                <Typography variant="body2" sx={{ color: 'white', mx: 1 }}>
                                                    {totalPages > 0 ? (
                                                        `Page ${currentPage || 1} of ${Math.ceil(totalPages / pageSize)}`
                                                    ) : (
                                                        'No data'
                                                    )}
                                                </Typography>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    disabled={totalPages === 0 || (currentPage || 1) >= Math.ceil(totalPages / pageSize)}
                                                    onClick={() => handlePageChange((currentPage || 1) + 1)}
                                                    sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                                                >
                                                    Next
                                                </Button>
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        ) : (
                            /* List View */
                            <Box>
                                <DataTable
                                    customStyles={{
                                        headCells: {
                                            style: {
                                                fontSize: "15px",
                                                fontWeight: "bold",
                                                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                                                color: 'black'
                                            },
                                        },
                                        rows: {
                                            style: {
                                                backgroundColor: 'transparent',
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                                }
                                            }
                                        },
                                        cells: {
                                            style: {
                                                color: 'white',
                                            }
                                        }
                                    }}
                                    columns={columns}
                                    data={data}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={totalPages || 0}
                                    paginationCurrentPage={currentPage || 1}
                                    paginationDefaultPage={currentPage || 1}
                                    onChangePage={handlePageChange}
                                    paginationPerPage={pageSize}
                                    paginationRowsPerPageOptions={[10, 20, 50]}
                                    onChangeRowsPerPage={(currentRowsPerPage) =>
                                        setPageSize(currentRowsPerPage)
                                    }
                                    paginationComponentOptions={{
                                        rowsPerPageText: 'Rows per page:',
                                        rangeSeparatorText: 'of',
                                        selectAllRowsItem: true,
                                        selectAllRowsItemText: 'All',
                                    }}
                                    progressPending={isLoading || !loaderDelay}
                                    progressComponent={<Loader />}
                                    className="custom"
                                />
                            </Box>
                        )}
                    </Paper>
                </Stack>
            </Fade>

            {/* Chat dialog */}
            <ChatDialog
                open={openChat}
                onClose={() => setOpenChat(false)}
                messages={messages}
                userId={userId}
                friendId={selectedFriend.id}
                friendName={selectedFriend.name}
                onDeleteMessage={handleDeleteMessage}
                onClearHistory={handleClearHistory}
            />
        </Container>
    );
}
